import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, Button, Box, Typography,
    TextField, Alert, Slide, IconButton, InputAdornment, CircularProgress,
    Checkbox, FormControlLabel
} from '@mui/material';
import {
    Close, Email, Visibility, VisibilityOff, Lock, CheckCircle, DeleteOutline
} from '@mui/icons-material';
import axios from 'axios';
import { API_CONFIG } from '../config/authConfig';
import { clearUserSessionFully, saveUserSession, getHomeRoute } from '../utils/sessionManager';
import { rememberDeviceAccounts } from '../utils/deviceAccounts';
import { useNavigate } from 'react-router-dom';

const inputStyles = {
    mb: 2.5,
    '& .MuiOutlinedInput-root': {
        background: '#1a1a1a',
        borderRadius: '12px',
        color: '#fff',
        '&:hover fieldset': { borderColor: '#6366f1' },
        '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: 2 },
        '& fieldset': { borderColor: '#374151' }
    },
    '& .MuiInputLabel-root': {
        color: '#9ca3af',
        '&.Mui-focused': { color: '#6366f1' }
    }
};

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess?: (userData: any) => void;
    /** Почта привязана к нескольким учетным записям: их сессии открыты и записаны в список устройства */
    onAccountsOpened?: () => void;
}

const MailLoginDialog: React.FC<Props> = ({ open, onClose, onSuccess, onAccountsOpened }) => {
    const [mail, setMail] = useState(() => localStorage.getItem('zerde_saved_mail_email') || '');
    const [password, setPassword] = useState(() => localStorage.getItem('zerde_saved_mail_password') || '');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMail, setRememberMail] = useState(true);
    const [hasSaved, setHasSaved] = useState(() => Boolean(localStorage.getItem('zerde_saved_mail_email')));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (open) {
            const savedEmail = localStorage.getItem('zerde_saved_mail_email');
            const savedPass = localStorage.getItem('zerde_saved_mail_password');
            if (savedEmail) setMail(savedEmail);
            if (savedPass) setPassword(savedPass);
            setHasSaved(Boolean(savedEmail));
        }
    }, [open]);

    const handleClose = () => {
        if (loading) return;
        setError(null);
        onClose();
    };

    const handleClearSaved = () => {
        localStorage.removeItem('zerde_saved_mail_email');
        localStorage.removeItem('zerde_saved_mail_password');
        setMail('');
        setPassword('');
        setHasSaved(false);
    };

    const handleLogin = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!mail.trim()) {
            setError('Введите корпоративную почту (@zerdegroup.kz)');
            return;
        }
        if (!password.trim()) {
            setError('Введите пароль от корпоративной почты');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const rawEmail = mail.trim().toLowerCase();
            const cleanEmail = rawEmail.includes('@') ? rawEmail : `${rawEmail}@zerdegroup.kz`;

            const response = await axios.post(`${API_CONFIG.AUTH_V2_URL}/login-via-mail`, {
                email: cleanEmail,
                mailPassword: password
            });

            // Запоминаем данные на этом компьютере для быстрого входа (почта уже подтверждена сервером)
            const persistMailCredentials = () => {
                if (rememberMail) {
                    localStorage.setItem('zerde_saved_mail_email', cleanEmail);
                    localStorage.setItem('zerde_saved_mail_password', password);
                } else {
                    localStorage.removeItem('zerde_saved_mail_email');
                    localStorage.removeItem('zerde_saved_mail_password');
                }
            };

            if (response.data?.success && response.data?.multipleAccounts && Array.isArray(response.data.accounts)) {
                // Сессии всех учетных записей почты открыты: запоминаем их и показываем экран аккаунтов
                persistMailCredentials();
                rememberDeviceAccounts(response.data.accounts);
                onClose();
                onAccountsOpened?.();
            } else if (response.data?.success && response.data?.sessionToken) {
                // Одна учетная запись: сохраняем сессию и входим сразу
                clearUserSessionFully();
                saveUserSession(response.data);
                if (response.data.avatar) {
                    localStorage.setItem('userAvatar', response.data.avatar);
                }

                persistMailCredentials();

                if (onSuccess) {
                    onSuccess(response.data);
                } else {
                    navigate(getHomeRoute(response.data), { replace: true });
                }
                handleClose();
            } else {
                setError(response.data?.message || 'Не удалось выполнить вход через почту');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Ошибка авторизации в корпоративной почте. Проверьте адрес и пароль.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                TransitionComponent={Slide}
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '20px',
                        background: '#0a0a0a',
                        border: '1px solid #374151',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
                    }
                }}
            >
                <DialogTitle sx={{
                    textAlign: 'center',
                    background: '#111',
                    color: 'white',
                    py: 2.5,
                    borderBottom: '1px solid #262626',
                    position: 'relative'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                        <Email sx={{ color: '#818cf8' }} />
                        <Typography variant="h6" fontWeight="bold">Корпоративная почта</Typography>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            position: 'absolute',
                            right: 12,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#9ca3af',
                            '&:hover': { color: '#fff', bgcolor: 'rgba(255, 255, 255, 0.08)' }
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3, pt: 3.5, bgcolor: '#0a0a0a' }}>
                    <form onSubmit={handleLogin}>
                        <TextField
                            label="Корпоративный email"
                            fullWidth
                            value={mail}
                            onChange={(e) => setMail(e.target.value.toLowerCase())}
                            disabled={loading}
                            autoFocus
                            placeholder="ap8899 или user@zerdegroup.kz"
                            sx={{ ...inputStyles, mt: 3 }}
                        />

                        <TextField
                            label="Пароль от почты"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            sx={inputStyles}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ color: '#9ca3af' }}>
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Галочка запоминания данных на этом компьютере */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: 0.5 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rememberMail}
                                        onChange={(e) => setRememberMail(e.target.checked)}
                                        sx={{ color: '#6366f1', '&.Mui-checked': { color: '#6366f1' }, p: 0.5 }}
                                    />
                                }
                                label={<Typography sx={{ color: '#9ca3af', fontSize: '0.82rem' }}>Запомнить данные</Typography>}
                                sx={{ m: 0 }}
                            />
                            {hasSaved && (
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={handleClearSaved}
                                    sx={{ color: '#f87171', fontSize: '0.78rem', textTransform: 'none', p: 0, minWidth: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                                >
                                    Забыть
                                </Button>
                            )}
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>
                                {error}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading || !mail.trim() || !password.trim()}
                            sx={{
                                py: 1.4,
                                borderRadius: '12px',
                                background: '#6366f1',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                '&:hover': { background: '#4f46e5' },
                                '&:disabled': { background: '#262626', color: '#525252' }
                            }}
                        >
                            {loading ? <CircularProgress size={22} color="inherit" /> : 'Войти через почту'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MailLoginDialog;
